import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  RefreshCw,
  Clock,
  Check,
  X,
  ArrowLeft,
  Loader2,
  Calendar,
  MessageCircle,
  ChevronRight,
  Send,
  Instagram,
  Twitter,
} from "lucide-react";
import BottomNavigation from "../components/BottomNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { value: "telegram", label: "Telegram" },
  { value: "privacy", label: "Privacy" },
  { value: "onlyfans", label: "OnlyFans" },
  { value: "twitter", label: "X / Twitter" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
];

const STATUS_LABELS = {
  pending: { label: "Pendente", color: "border-yellow-500/50 text-yellow-400" },
  accepted: { label: "Aceito", color: "border-emerald-500/50 text-emerald-400" },
  rejected: { label: "Recusado", color: "border-red-500/50 text-red-400" },
  scheduled: { label: "Agendado", color: "border-blue-500/50 text-blue-400" },
  completed: { label: "Concluído", color: "border-green-500/50 text-green-400" },
  cancelled: { label: "Cancelado", color: "border-gray-500/50 text-gray-400" },
};

const ExchangesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("active");
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExchanges();
  }, []);

  const fetchExchanges = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/exchanges?Authorization=Bearer ${token}`);
      setExchanges(response.data.exchanges || []);
    } catch (error) {
      console.error("Failed to fetch exchanges:", error);
      toast.error("Erro ao carregar trocas");
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (exchangeId, accept) => {
    try {
      await axios.put(
        `${API}/exchanges/${exchangeId}/respond?Authorization=Bearer ${token}`,
        { accept, message: "" }
      );
      toast.success(accept ? "Troca aceita!" : "Troca recusada");
      fetchExchanges();
    } catch (error) {
      toast.error("Erro ao responder solicitação");
    }
  };

  const getPhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  const activeExchanges = exchanges.filter(ex => ["pending", "accepted", "scheduled"].includes(ex.status));
  const historyExchanges = exchanges.filter(ex => ["completed", "rejected", "cancelled"].includes(ex.status));

  const ExchangeCard = ({ exchange }) => {
    const status = STATUS_LABELS[exchange.status] || STATUS_LABELS.pending;
    const isPending = exchange.status === "pending" && !exchange.is_requester;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all"
      >
        <div className="flex items-start gap-4">
          {/* User Photo */}
          <div className="w-14 h-14 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
            {getPhoto(exchange.other_user?.profile_photo) ? (
              <img 
                src={getPhoto(exchange.other_user?.profile_photo)} 
                alt={exchange.other_user?.display_name} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/30">
                <Users className="w-7 h-7" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-white">{exchange.other_user?.display_name}</div>
                <div className="text-sm text-white/50">@{exchange.other_user?.username}</div>
              </div>
              <Badge variant="outline" className={status.color}>
                {status.label}
              </Badge>
            </div>
            
            {/* Platforms */}
            <div className="flex items-center gap-2 text-sm text-white/70 mb-3">
              <Badge className="bg-[#FD5564]/20 text-[#FD5564] border-0">
                {exchange.is_requester ? exchange.requester_platform : exchange.target_platform}
              </Badge>
              <RefreshCw className="w-4 h-4 text-white/30" />
              <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                {exchange.is_requester ? exchange.target_platform : exchange.requester_platform}
              </Badge>
            </div>

            {/* Dates if scheduled */}
            {exchange.status === "scheduled" && (
              <div className="flex items-center gap-4 text-sm mb-3">
                {exchange.requester_date && (
                  <div className="flex items-center gap-1 text-white/60">
                    <Calendar className="w-4 h-4" />
                    <span>Você: {new Date(exchange.requester_date).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
                {exchange.target_date && (
                  <div className="flex items-center gap-1 text-white/60">
                    <Calendar className="w-4 h-4" />
                    <span>Parceiro: {new Date(exchange.target_date).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Message */}
            {exchange.message && (
              <div className="p-2 bg-white/5 rounded-lg text-sm text-white/60 mb-3">
                "{exchange.message}"
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              {isPending && (
                <>
                  <Button
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => handleRespond(exchange.id, true)}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Aceitar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    onClick={() => handleRespond(exchange.id, false)}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Recusar
                  </Button>
                </>
              )}
              
              {exchange.status === "accepted" && (
                <Button
                  size="sm"
                  className="bg-[#FD5564] hover:bg-[#FF7854]"
                  onClick={() => navigate(`/exchanges/${exchange.id}/schedule`)}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Agendar Data
                </Button>
              )}
              
              {exchange.status === "scheduled" && (
                <Button
                  size="sm"
                  className="bg-[#FD5564] hover:bg-[#FF7854]"
                  onClick={() => navigate(`/exchanges/${exchange.id}`)}
                >
                  Ver Detalhes
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/discovery")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Trocas de Divulgação</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchExchanges}
            className="text-white/70"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-white/5 border border-white/10 mb-6">
              <TabsTrigger value="active" className="flex-1 data-[state=active]:bg-[#FD5564]">
                <RefreshCw className="w-4 h-4 mr-2" />
                Ativas ({activeExchanges.length})
              </TabsTrigger>
              <TabsTrigger value="history" className="flex-1 data-[state=active]:bg-[#FD5564]">
                <Clock className="w-4 h-4 mr-2" />
                Histórico ({historyExchanges.length})
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
              </div>
            ) : (
              <>
                <TabsContent value="active">
                  <div className="space-y-3">
                    <AnimatePresence>
                      {activeExchanges.length > 0 ? (
                        activeExchanges.map((ex) => (
                          <ExchangeCard key={ex.id} exchange={ex} />
                        ))
                      ) : (
                        <div className="text-center py-16">
                          <RefreshCw className="w-12 h-12 text-white/20 mx-auto mb-4" />
                          <p className="text-white/50 mb-4">Nenhuma troca ativa</p>
                          <p className="text-white/30 text-sm mb-6">
                            Conecte-se com outros criadores para solicitar trocas de divulgação
                          </p>
                          <Button
                            className="bg-[#FD5564] hover:bg-[#FF7854]"
                            onClick={() => navigate("/connections")}
                          >
                            Ver Conexões
                          </Button>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </TabsContent>

                <TabsContent value="history">
                  <div className="space-y-3">
                    <AnimatePresence>
                      {historyExchanges.length > 0 ? (
                        historyExchanges.map((ex) => (
                          <ExchangeCard key={ex.id} exchange={ex} />
                        ))
                      ) : (
                        <div className="text-center py-16">
                          <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
                          <p className="text-white/50">Nenhum histórico ainda</p>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

// New Exchange Request Page
export const NewExchangePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { token } = useAuth();
  const [targetUser, setTargetUser] = useState(null);
  const [myPlatform, setMyPlatform] = useState("");
  const [targetPlatform, setTargetPlatform] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTargetUser();
  }, [userId]);

  const fetchTargetUser = async () => {
    try {
      // For now, we'll get from connections
      const response = await axios.get(`${API}/connections?Authorization=Bearer ${token}`);
      const connection = response.data.connections?.find(c => c.user?.id === userId);
      if (connection) {
        setTargetUser(connection.user);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!myPlatform || !targetPlatform) {
      toast.error("Selecione as plataformas");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/exchanges/request?Authorization=Bearer ${token}`,
        {
          target_user_id: userId,
          my_platform: myPlatform,
          target_platform: targetPlatform,
          message
        }
      );
      toast.success("Solicitação enviada!");
      navigate("/exchanges");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar solicitação");
    } finally {
      setSubmitting(false);
    }
  };

  const getPhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white/70"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Solicitar Troca</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Target User */}
          {targetUser && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-white/10">
                  {getPhoto(targetUser.profile_photo) ? (
                    <img src={getPhoto(targetUser.profile_photo)} alt={targetUser.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">
                      <Users className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-semibold text-lg">{targetUser.display_name}</div>
                  <div className="text-white/50">@{targetUser.username}</div>
                </div>
              </div>
            </div>
          )}

          {/* Platform Selection */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Minha plataforma (o que vou oferecer)
              </label>
              <Select value={myPlatform} onValueChange={setMyPlatform}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Selecione sua plataforma" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-center">
              <div className="w-10 h-10 rounded-full bg-[#FD5564]/20 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-[#FD5564]" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Plataforma do parceiro (o que quero em troca)
              </label>
              <Select value={targetPlatform} onValueChange={setTargetPlatform}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Selecione a plataforma" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Mensagem (opcional)
              </label>
              <Textarea
                placeholder="Escreva uma mensagem para o parceiro..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
              />
            </div>

            <Button
              className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 h-12"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Enviar Solicitação
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExchangesPage;
